import { Injector } from '@angular/core';
import { firstValueFrom, Observable, Subscription } from 'rxjs';
import { fabric } from 'fabric';
import { ItemModel } from '../../../../models/itemModel';
import { QuestionsService } from '../../../../services/questions.service';
import { CustomErrorCodes } from '../../../../../custom_error_codes.constants';
import { QuestionContainerComponent } from '../../question-container/question-container.component';
import { FrameItem, QuestionState } from '../../../../models/session';
import {
  QUESTIONS_DEFAULT_HEIGHT,
  QUESTIONS_DEFAULT_WIDTH,
} from '../../iframe/additional-apps.utils';
import {
  AccessState,
  BoardQuestionConfigurations,
  BoardQuestionType,
  QuestionVisibility,
} from './board-question-configurations';
import { CreationContext } from './board-item-configurations';
import { BoardItemComponentClass, FabricControls } from './board.item';

const QuestionInvisibleControls = Object.values(FabricControls);

// Hide all rotation controls and middle controls for questions
export class BoardQuestion extends BoardItemComponentClass<
  BoardQuestionConfigurations,
  QuestionContainerComponent
>(fabric.Rect) {
  AngularComponentClass = QuestionContainerComponent;

  private contentSizeSubscription?: Subscription;

  private questionsService: QuestionsService;

  constructor(
    injector: Injector,
    configurations: BoardQuestionConfigurations,
    creationContext: CreationContext,
  ) {
    super(
      injector,
      configurations.questionType === BoardQuestionType.BoardQuestion
        ? ItemModel.Question
        : ItemModel.Note,
      configurations,
      QuestionInvisibleControls,
      creationContext,
    );

    this.transparentCorners = true;
    this.hasRotatingPoint = false;

    this.questionsService = injector.get<QuestionsService>(QuestionsService);
  }

  protected onFabricAdded() {
    super.onFabricAdded();

    this.loadQuestion();
  }

  bindAngularComponentOutputs() {
    super.bindAngularComponentOutputs();

    if (!this.componentRef) {
      return;
    }

    this.contentSizeSubscription?.unsubscribe();
    this.contentSizeSubscription = this.componentRef.instance.contentSizeChanged.subscribe(
      ({ width, height }) => {
        if (!this.isOnStaticCanvas()) {
          this.set({ width, height } as Partial<this>);
          this.canvas?.requestRenderAll();
          this.syncHtmlWithFabric();
        }
      },
    );
  }

  protected setStateFromConfigurations(configurations: BoardQuestionConfigurations) {
    // Overriding width and height update because it should only depend on the size of the content
    configurations.height = this.height ?? QUESTIONS_DEFAULT_HEIGHT;
    configurations.width = this.width ?? QUESTIONS_DEFAULT_WIDTH;

    const previousVisibility = this.configurations.visibility;

    super.setStateFromConfigurations(configurations);

    if (previousVisibility !== configurations.visibility) {
      this.loadQuestion();
    }
  }

  override openLock() {
    this.lockMovementX = false;
    this.lockMovementY = false;
    this.lockRotation = true;
    this.lockScalingX = true;
    this.lockScalingY = true;
    this.lockUniScaling = true;
    this.lockSkewingX = true;
    this.lockSkewingY = true;
    this.lockScalingFlip = true;
  }

  getZIndexObservable$(): Observable<number> {
    return this.getDefaultZIndexObservable$();
  }

  override toFrameItem(): FrameItem {
    const encodedBoardItem = super.toFrameItem();
    return {
      ...encodedBoardItem,
      model:
        this.configurations.questionType === BoardQuestionType.BoardQuestion
          ? ItemModel.Question
          : ItemModel.Note,
      question_state: new QuestionState({
        show_hint: this.configurations.showHint,
        show_answer: this.configurations.showAnswer,
        // When the answers are shown, we reset the answers
        answers: this.configurations.showAnswer
          ? {}
          : Object.fromEntries(this.configurations.answers),
        visibility: this.configurations.visibility,
      }),
      content_id: this.configurations.contentId,
    };
  }

  // The following methods shouldn't be here, this component is intended only to have the fabric interactions code
  // but we are doing this here just to not refactor the question view container because it is so complex
  // and currently such changes are not worth it since questions and notes are rarely used.
  async loadQuestion() {
    if (this.isOnStaticCanvas()) {
      return;
    }

    const data = await firstValueFrom(
      this.questionsService.getQuestionData(
        this.configurations.contentId,
        this.configurations.questionType,
      ),
    );

    if (!data) {
      return;
    }

    if (data.status_code == CustomErrorCodes.CANT_ACCESS_QUESTION) {
      this.configurations.accessState = AccessState.NotInQuestion;
    }
    if (data.status_code == CustomErrorCodes.NOT_IN_COURSE) {
      this.configurations.accessState = AccessState.NotInCourse;
    }
    if (data.questions || data.note) {
      this.configurations.question =
        this.configurations.questionType === BoardQuestionType.BoardQuestion
          ? data.questions
          : data.note;

      if (this.configurations.question?.acl.visibility) {
        this.configurations.visibility = this.configurations.question?.acl
          .visibility as QuestionVisibility;
      }
    }

    this.bindAngularComponentInputs();
  }
}
